import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BookOpen, Search, Eye } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const Knowledge = () => {
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/knowledge/articles`);
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const runSearch = async (term) => {
    if (!term) {
      fetchArticles();
      return;
    }
    try {
      const response = await axios.get(`${API}/knowledge/articles?search=${encodeURIComponent(term)}`);
      setArticles(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const handleSearch = () => {
    clearTimeout(debounceRef.current);
    runSearch(searchTerm);
  };

  const handleArticleClick = async (article) => {
    try {
      const response = await axios.get(`${API}/knowledge/articles/${article.id}`);
      setSelectedArticle(response.data);
    } catch (error) {
      console.error('Failed to fetch article:', error);
    }
  };

  if (selectedArticle) {
    return (
      <div 
        data-testid="article-detail" 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
        style={bgStyle(IMAGES.ROUTER_KNOWLEDGE)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
          <div className="max-w-4xl mx-auto space-y-8">
            <Button
              data-testid="back-to-list-btn"
              variant="ghost"
              onClick={() => setSelectedArticle(null)}
              className="text-[#e67e22] hover:text-[#e67e22]/80 hover:bg-black/60"
            >
              ← Back to Articles
            </Button>

            <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
              <CardHeader>
                <div className="flex items-center gap-2 text-xs text-[#a3a3a3] mb-2">
                  <span className="px-2 py-1 bg-[#e67e22]/20 text-[#e67e22] rounded uppercase font-bold">
                    {selectedArticle.category}
                  </span>
                  <Eye className="w-4 h-4" />
                  <span>{selectedArticle.views} views</span>
                </div>
                <CardTitle className="text-3xl text-white">{selectedArticle.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-invert max-w-none">
                  <p className="text-[#d6d6d6] leading-relaxed whitespace-pre-wrap">{selectedArticle.content}</p>
                </div>
                <div className="flex flex-wrap gap-2 mt-6">
                  {selectedArticle.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-black/50 border border-[#3a3a3a] text-[#a3a3a3] rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-10 flex justify-center">
                  <Button
                    data-testid="back-to-list-btn-bottom"
                    variant="ghost"
                    onClick={() => setSelectedArticle(null)}
                    className="text-[#e67e22] hover:text-[#e67e22]/80 hover:bg-black/60"
                  >
                    ← Back to Articles
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="knowledge-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.ROUTER_KNOWLEDGE)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-6xl mx-auto space-y-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">LEARN</div>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-[#e67e22] mb-2">
              KNOWLEDGE BASE
            </h1>
            <p className="text-lg text-[#e67e22]">Browse articles, guides, and CNC best practices</p>
          </div>

          <Card className="bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
            <CardContent className="pt-6">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#a3a3a3]" />
                  <Input
                    data-testid="search-input"
                    type="text"
                    placeholder="Search articles, tags, or topics..."
                    value={searchTerm}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSearchTerm(val);
                      clearTimeout(debounceRef.current);
                      debounceRef.current = setTimeout(() => runSearch(val), 300);
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10 bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3]"
                  />
                </div>
                <Button
                  data-testid="search-btn"
                  onClick={handleSearch}
                  className="bg-[#e67e22] hover:bg-[#e67e22]/90 text-black font-bold"
                >
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article, index) => (
              <Card
                key={article.id}
                data-testid={`article-${index}`}
                onClick={() => handleArticleClick(article)}
                className="bg-black/60 backdrop-blur-sm border-[#3a3a3a] hover:border-[#e67e22]/50 transition-colors cursor-pointer group"
              >
                <CardHeader>
                  <div className="flex items-center gap-2 text-xs text-[#a3a3a3] mb-2">
                    <span className="px-2 py-1 bg-[#e67e22]/20 text-[#e67e22] rounded uppercase font-bold">
                      {article.category}
                    </span>
                    <Eye className="w-4 h-4" />
                    <span>{article.views}</span>
                  </div>
                  <CardTitle className="text-white group-hover:text-[#e67e22] transition-colors">
                    {article.title}
                  </CardTitle>
                  <CardDescription className="text-[#a3a3a3] line-clamp-2">{article.content}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.slice(0, 3).map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-2 py-1 bg-black/50 border border-[#3a3a3a] text-[#a3a3a3] rounded text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {articles.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-[#a3a3a3] mx-auto mb-4" />
              <p className="text-[#a3a3a3]">No articles found</p>
            </div>
          )}
        </div>
    </div>
  );
};

export default Knowledge;
